import React from 'react';
// @ts-ignore
import Highlighter from 'react-highlight-words';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { Close, Done, DoneAll, QueryBuilder } from '@material-ui/icons';
import { Button } from '@material-ui/core';
import { MessageStatusEnum } from '~/enums/conversations';
import { Message } from '~/interfaces/conversations';
import { dayjs, useTranslation } from '~/dependencies';
import { log } from '~/helpers';
import {
  Container,
  Body,
  Avatar,
  Footer,
  MessageComponent,
  Scheduled,
} from './styles';

// Returns e.g., "9:21 PM"
function formatTimestamp(date: string, userTz: string) {
  return dayjs(date)
    .tz(userTz)
    .format('h:mm A');
}

function composeMessageInfo(
  timestamp: string,
  senderAgentDisplayName: string | undefined,
  senderId: string | null | undefined = null
) {
  const parts = [timestamp];
  if (senderAgentDisplayName) parts.push(senderAgentDisplayName);
  if (senderId) parts.push(senderId);
  return parts.join(' • ');
}

const getMessageStatusIcon = (status: number | null) => {
  switch (status) {
    case MessageStatusEnum.SENT:
      return <Done className="icon" />;
    case MessageStatusEnum.DELIVERED:
      return <DoneAll className="icon delivered" />;
    case MessageStatusEnum.FAILED:
      return <Close className="icon failed" />;
    case MessageStatusEnum.SCHEDULED:
      return <QueryBuilder className="icon" />;
    default:
      return null;
  }
};

const RECEIVED_CODE = 1;
const SENT_CODE = 2;

type RowProps = {
  item: Message;
  userTz: string;
  searchValue: string;
  cancelMessage: (data: any) => void;
};

export default function Row({
  item,
  userTz,
  searchValue,
  cancelMessage,
}: RowProps) {
  const {
    id,
    conversationId,
    direction,
    createdAt,
    message,
    senderAgentDisplayName,
    statuses,
    scheduledFor,
    deliveredAt,
    senderId,
  } = item;
  // HOOKS
  const { t } = useTranslation();

  // CONST's
  const isSentCode = direction === SENT_CODE;
  const messageStatus =
    statuses && statuses.length > 0 ? statuses[0].status : -1;

  const renderMessageDateTime = () => {
    if (scheduledFor && deliveredAt === null) {
      const scheduledDateTime = composeMessageInfo(
        `${t('screens.conversation.messages.scheduledTo')}: ${dayjs(
          scheduledFor
        )
          .tz(userTz)
          .format('M/D/YYYY h:mm:ss A')}`,
        senderAgentDisplayName
      );

      return (
        <Scheduled>
          <span>{scheduledDateTime}</span>
          <Button
            color="primary"
            size="small"
            disableRipple
            disableElevation
            onClick={() => cancelMessage({ conversationId, messageId: id })}>
            {t('components.sendSMS.cancel')}
          </Button>
        </Scheduled>
      );
    }

    return composeMessageInfo(
      formatTimestamp(createdAt, userTz),
      direction === SENT_CODE ? senderAgentDisplayName : undefined,
      parsePhoneNumberFromString(String(senderId))?.formatInternational() ||
        senderId
    );
  };

  return (
    <Container onDoubleClick={() => log.debug('Message details: ', item)}>
      {direction === SENT_CODE && (
        <Avatar marginRight style={{ opacity: 0 }}>
          <span>-</span>
        </Avatar>
      )}
      <MessageComponent
        isSender={isSentCode}
        isScheduled={scheduledFor && deliveredAt === null}>
        <Body isSender={isSentCode}>
          <Highlighter
            highlightClassName="highlighter"
            searchWords={[searchValue]}
            autoEscape
            textToHighlight={message}
          />
        </Body>
        <Footer isSender={isSentCode}>
          {renderMessageDateTime()}
          {isSentCode ? getMessageStatusIcon(messageStatus) : null}
        </Footer>
      </MessageComponent>
      {direction === RECEIVED_CODE && (
        <Avatar marginLeft style={{ opacity: 0 }}>
          <span>-</span>
        </Avatar>
      )}
    </Container>
  );
}
